import type { FC, FocusEvent, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { MotionValue } from 'motion/react'

import Image from 'next/image'
import { useEffect, useRef } from 'react'
import { motion, useMotionValue, useScroll, useTransform } from 'motion/react'
import { cn } from '@/lib/utils'
import SectionHeading from '@/components/landing/section-heading'
import SpotlightCard from '@/components/generals/motion/spotlight-card'
import PhoneForecast from '@/components/landing/solutions/phone-forecast'
import BalanceVisual from '@/components/landing/solutions/balance-visual'
import TradeVisual from '@/components/landing/solutions/trade-visual'
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion'

import {
    ArrowRight,
    BatteryCharging,
    BellRing,
    ChartColumn,
    ChartLine,
    FileCheck2,
    HeartPulse,
    Repeat
} from 'lucide-react'

interface PipelineLayout {
    distance: number
    centers: number[]
}

interface StageMotion {
    offset: MotionValue<number>
    focus: MotionValue<number>
}

interface CardTagProps {
    icon: LucideIcon
    label: string
    tone?: 'light' | 'dark'
}

interface HandoffProps {
    icon: LucideIcon
    label: string
    text: string
    tone?: 'light' | 'dark'
}

interface StageProps {
    stage: StageMotion
    pinned: boolean
    tone?: 'light' | 'dark'
    background?: ReactNode
    visual: ReactNode
    visualClassName?: string
    children: ReactNode
}

interface RailProps {
    fill: MotionValue<number>
}

interface RailNodeProps {
    fill: MotionValue<number>
    at: number
    label: string
    note: string
    className?: string
}

const extras = [
    {
        icon: BellRing,
        title: 'Curtailment alerts',
        text: 'Know hours ahead when output will outrun the grid, with a plan for where the surplus should go.'
    },
    {
        icon: HeartPulse,
        title: 'Asset health',
        text: 'Spot the turbine, string or battery cell that is underperforming before it costs you a peak.'
    },
    {
        icon: FileCheck2,
        title: 'Audit-ready reports',
        text: 'Every dispatch decision is logged with the forecast and price that justified it.'
    }
]

const railStages = [
    { label: 'Forecast', note: 'Predicts the surplus', className: 'items-start text-left' },
    { label: 'Balance', note: 'Stores it', className: 'items-center text-center' },
    { label: 'Trade', note: 'Sells it at the peak', className: 'items-end text-right' }
]

// The track holds still for a moment at both ends of the pinned scroll.
const LINGER = 0.05

const clamp01 = (value: number): number => Math.min(Math.max(value, 0), 1)

const toTravel = (progress: number): number => clamp01((progress - LINGER) / (1 - LINGER * 2))

// Maps sideways travel onto the rail so each node lights as its panel reaches the middle.
const railFill = (travel: number, centers: number[]): number => {
    const [first, second, third] = centers.length === 3 ? centers : [0, 0.5, 1]

    if (travel <= first) return 0
    if (travel <= second) return ((travel - first) / Math.max(second - first, 0.0001)) * 0.5
    if (travel <= third) return 0.5 + ((travel - second) / Math.max(third - second, 0.0001)) * 0.5

    return 1
}

const useStageMotion = (index: number, travel: MotionValue<number>, layout: MotionValue<PipelineLayout>): StageMotion => {
    const offset = useTransform(() => {
        const { distance, centers } = layout.get()
        const center = centers[index]

        if (distance <= 0 || center === undefined) return 0

        return travel.get() - center
    })

    const focus = useTransform(offset, (value) => 1 - Math.min(Math.abs(value) / 0.45, 1))

    return { offset, focus }
}

const CardTag: FC<CardTagProps> = ({ icon: Icon, label, tone = 'light' }): ReactNode => {
    return (
        <span
            className={cn(
                'inline-flex items-center gap-2 rounded-full py-1 pl-1 pr-3 text-xs font-medium',
                tone === 'dark' ? 'bg-white/10 text-white ring-1 ring-white/15' : 'bg-background text-foreground ring-1 ring-black/5'
            )}
        >
            <span
                className={cn(
                    'flex size-6 items-center justify-center rounded-full',
                    tone === 'dark' ? 'bg-leaf text-forest' : 'bg-primary text-white'
                )}
            >
                <Icon className='size-3.5' aria-hidden='true' />
            </span>
            {label}
        </span>
    )
}

// Names the panel the work flows into next, so the relationship between stages is explicit.
const Handoff: FC<HandoffProps> = ({ icon: Icon, label, text, tone = 'light' }): ReactNode => {
    const isDark = tone === 'dark'

    return (
        <p className={cn('mt-auto flex items-center gap-3 pt-8 text-sm leading-snug', isDark ? 'text-forest-foreground/70' : 'text-muted-foreground')}>
            <span
                className={cn(
                    'flex size-9 shrink-0 items-center justify-center rounded-full ring-1',
                    isDark ? 'bg-white/10 text-leaf ring-white/15' : 'bg-accent text-accent-foreground ring-black/5'
                )}
            >
                <Icon className='size-4' aria-hidden='true' />
            </span>

            <span>
                <span className={cn('font-medium', isDark ? 'text-white' : 'text-foreground')}>{label}</span>{' '}
                {text}
            </span>
        </p>
    )
}

const Stage: FC<StageProps> = ({
    stage,
    pinned,
    tone = 'light',
    background,
    visual,
    visualClassName,
    children
}): ReactNode => {
    const scale = useTransform(stage.focus, [0, 1], [0.9, 1])
    const opacity = useTransform(stage.focus, [0, 1], [0.5, 1])
    const visualY = useTransform(stage.focus, [0, 1], [56, 0])

    return (
        <motion.article
            data-panel=''
            data-stage=''
            style={pinned ? { scale, opacity } : undefined}
            className={cn(
                'relative isolate overflow-hidden rounded-[2rem]',
                tone === 'dark' ? 'bg-forest text-white' : 'bg-white ring-1 ring-inset ring-black/5',
                pinned && 'lg:h-full lg:w-[min(72vw,56rem)] lg:shrink-0'
            )}
        >
            {background}

            <div
                className={cn(
                    'flex flex-col gap-8 p-6 sm:p-8 lg:grid lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1fr)] lg:gap-10 lg:p-10',
                    pinned && 'lg:h-full lg:grid-rows-[minmax(0,1fr)]'
                )}
            >
                <div className='flex flex-col'>
                    {children}
                </div>

                <motion.div style={pinned ? { y: visualY } : undefined} className={visualClassName}>
                    {visual}
                </motion.div>
            </div>
        </motion.article>
    )
}

const RailNode: FC<RailNodeProps> = ({ fill, at, label, note, className }): ReactNode => {
    const lit = useTransform(fill, [at - 0.04, at], [0, 1])
    const textOpacity = useTransform(lit, [0, 1], [0.45, 1])

    return (
        <li className={cn('flex flex-col gap-1.5', className)}>
            <span className='relative size-3.5 rounded-full bg-background ring-1 ring-foreground/20'>
                <motion.span
                    className='absolute inset-0 rounded-full bg-primary shadow-[0_0_0_5px_rgba(30,133,56,0.16)]'
                    style={{ opacity: lit, scale: lit }}
                />
            </span>

            <motion.span style={{ opacity: textOpacity }} className='mt-2 text-sm font-medium text-foreground'>
                {label}
            </motion.span>

            <motion.span style={{ opacity: textOpacity }} className='text-xs text-muted-foreground'>
                {note}
            </motion.span>
        </li>
    )
}

// Forecast, Balance and Trade joined by one line that fills as the panels slide past.
const PipelineRail: FC<RailProps> = ({ fill }): ReactNode => {
    const tip = useTransform(fill, (value) => `${value * 100}%`)

    return (
        <div aria-hidden='true' className='relative hidden w-full max-w-md shrink-0 pb-1 lg:block'>
            <div className='absolute inset-x-[7px] top-[7px] h-px bg-foreground/10'>
                <motion.div
                    className='absolute inset-0 origin-left bg-gradient-to-r from-leaf to-primary'
                    style={{ scaleX: fill }}
                />

                <motion.span
                    className='absolute top-1/2 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-leaf shadow-[0_0_12px_2px_rgba(87,199,106,0.65)]'
                    style={{ left: tip }}
                />
            </div>

            <ol className='relative flex justify-between'>
                {railStages.map((stage, index) => (
                    <RailNode
                        key={stage.label}
                        fill={fill}
                        at={index / (railStages.length - 1)}
                        label={stage.label}
                        note={stage.note}
                        className={stage.className}
                    />
                ))}
            </ol>
        </div>
    )
}

// On large screens the section pins and its panels travel sideways with the scroll.
// Smaller screens, and visitors who prefer reduced motion, get a calm vertical stack.
const SolutionsSection: FC = (): ReactNode => {
    const still = usePrefersReducedMotion()
    const pinned = !still
    const sectionRef = useRef<HTMLElement>(null)
    const viewportRef = useRef<HTMLDivElement>(null)
    const trackRef = useRef<HTMLDivElement>(null)
    const layout = useMotionValue<PipelineLayout>({ distance: 0, centers: [] })
    const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end end'] })

    const travel = useTransform(scrollYProgress, toTravel)
    const x = useTransform(() => -travel.get() * layout.get().distance)
    const fill = useTransform(() => railFill(travel.get(), layout.get().centers))

    const forecast = useStageMotion(0, travel, layout)
    const balance = useStageMotion(1, travel, layout)
    const trade = useStageMotion(2, travel, layout)
    const imageX = useTransform(forecast.offset, [-1, 1], [-140, 140])

    useEffect(() => {
        const section = sectionRef.current
        const viewport = viewportRef.current
        const track = trackRef.current

        if (!section || !viewport || !track) return

        const measure = (): void => {
            const distance = Math.max(track.offsetWidth - viewport.clientWidth, 0)
            const half = viewport.clientWidth / 2
            const centers = distance > 0
                ? Array.from(track.querySelectorAll<HTMLElement>('[data-stage]')).map((panel) => (panel.offsetLeft + panel.offsetWidth / 2 - half) / distance)
                : []

            layout.set({ distance, centers })
            section.style.setProperty('--pipeline-distance', `${Math.round(distance * 1.15)}px`)
        }

        measure()

        const observer = new ResizeObserver(measure)

        observer.observe(viewport)
        observer.observe(track)

        return () => observer.disconnect()
    }, [layout, pinned])

    // Keyboard users tabbing into a panel that is still off to the side are scrolled to it.
    const onFocus = (event: FocusEvent<HTMLDivElement>): void => {
        const section = sectionRef.current
        const viewport = viewportRef.current
        const { distance } = layout.get()

        if (!pinned || !section || !viewport || distance <= 0) return

        const panel = (event.target as HTMLElement).closest<HTMLElement>('[data-panel]')

        if (!panel) return

        const target = clamp01((panel.offsetLeft + panel.offsetWidth / 2 - viewport.clientWidth / 2) / distance)
        const progress = LINGER + target * (1 - LINGER * 2)
        const top = section.getBoundingClientRect().top + window.scrollY
        const scrollable = section.offsetHeight - window.innerHeight

        window.scrollTo({ top: top + progress * scrollable })
    }

    return (
        <section
            ref={sectionRef}
            id='solutions'
            aria-labelledby='solutions-title'
            className={cn(
                'relative scroll-mt-20 bg-background py-24 sm:py-32',
                pinned && 'lg:h-[calc(100svh_+_var(--pipeline-distance,_260svh))] lg:py-0'
            )}
        >
            <div className={cn(pinned && 'lg:sticky lg:top-0 lg:flex lg:h-svh lg:flex-col lg:overflow-hidden lg:pb-10 lg:pt-20')}>
                <div className='mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8'>
                    <div className='flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between'>
                        <SectionHeading
                            id='solutions-title'
                            eyebrow='Solutions'
                            title='Forecast. Balance. Trade.'
                            accent='Three jobs, one platform.'
                        />

                        {pinned && <PipelineRail fill={fill} />}
                    </div>
                </div>

                <div
                    ref={viewportRef}
                    onFocusCapture={onFocus}
                    className={cn('mt-12', pinned && 'lg:mt-10 lg:min-h-0 lg:flex-1 lg:overflow-clip')}
                >
                    <motion.div
                        ref={trackRef}
                        style={pinned ? { x } : undefined}
                        className={cn(
                            'relative mx-auto flex max-w-7xl flex-col gap-4 px-4 sm:px-6 lg:px-8',
                            pinned && 'lg:mx-0 lg:h-full lg:w-max lg:max-w-none lg:flex-row lg:gap-6 lg:px-[max(2rem,calc((100vw_-_80rem)/2_+_2rem))] lg:will-change-transform'
                        )}
                    >
                        <div
                            data-panel=''
                            className={cn('max-w-xl pb-4', pinned && 'lg:flex lg:w-[21rem] lg:max-w-none lg:shrink-0 lg:flex-col lg:justify-between lg:py-2')}
                        >
                            <p className='text-base leading-relaxed text-muted-foreground lg:text-lg'>
                                Gridora replaces the patchwork of weather feeds, spreadsheets and late phone calls with one system that plans the energy day for you. Each stage hands its answer to the next.
                            </p>

                            {pinned && (
                                <p aria-hidden='true' className='mt-8 hidden items-center gap-3 text-xs font-medium uppercase tracking-[0.14em] text-foreground/60 lg:flex'>
                                    Scroll to follow the flow
                                    <span className='relative h-px w-14 overflow-hidden bg-foreground/15'>
                                        <span className='flow-cue-line absolute inset-y-0 left-0 w-1/2 bg-primary' />
                                    </span>
                                </p>
                            )}
                        </div>

                        <Stage
                            stage={forecast}
                            pinned={pinned}
                            tone='dark'
                            background={(
                                <>
                                    <motion.div
                                        aria-hidden='true'
                                        className='absolute inset-y-0 -left-[12%] -right-[12%] -z-20'
                                        style={pinned ? { x: imageX } : undefined}
                                    >
                                        <Image
                                            src='/images/hero-turbine.jpg'
                                            alt=''
                                            fill
                                            sizes='(min-width: 1024px) 80vw, 100vw'
                                            className='object-cover object-[50%_75%]'
                                        />
                                    </motion.div>

                                    <div aria-hidden='true' className='absolute inset-0 -z-10 bg-gradient-to-b from-forest via-forest/75 to-forest/20 lg:bg-gradient-to-r lg:from-forest lg:via-forest/80 lg:to-forest/10' />
                                </>
                            )}
                            visualClassName='-mb-28 flex justify-center sm:-mb-24 lg:mb-0 lg:items-start'
                            visual={<PhoneForecast />}
                        >
                            <div className='flex items-center justify-between gap-4'>
                                <CardTag icon={ChartLine} label='Forecast' tone='dark' />
                                <span className='font-mono text-xs text-white/50'>01 / 03</span>
                            </div>

                            <h3 className='mt-6 max-w-md font-heading text-3xl font-light leading-tight tracking-[-0.03em] sm:text-4xl lg:text-3xl xl:text-4xl'>
                                See demand and generation coming, a week ahead.
                            </h3>

                            <p className='mt-4 max-w-md text-[15px] leading-relaxed text-forest-foreground/75'>
                                Weather-aware forecasts at 15-minute resolution for every asset you run. Gridora learns each site, from icing turbines to hazy afternoons, and shows you how sure it is.
                            </p>

                            <Handoff
                                icon={ArrowRight}
                                label='Next, Balance.'
                                text='The surplus it predicts becomes the battery plan.'
                                tone='dark'
                            />
                        </Stage>

                        <Stage
                            stage={balance}
                            pinned={pinned}
                            visualClassName='lg:self-center'
                            visual={<BalanceVisual />}
                        >
                            <div className='flex items-center justify-between gap-4'>
                                <CardTag icon={BatteryCharging} label='Balance' />
                                <span className='font-mono text-xs text-muted-foreground'>02 / 03</span>
                            </div>

                            <h3 className='mt-6 max-w-md font-heading text-3xl font-light leading-tight tracking-[-0.03em] text-foreground sm:text-4xl lg:text-3xl xl:text-4xl'>
                                Charge on the surplus. Discharge into the peak.
                            </h3>

                            <p className='mt-4 max-w-md text-[15px] leading-relaxed text-muted-foreground'>
                                Dispatch plans that respect cycle limits, warranty terms and grid constraints, ready for one-click approval or autopilot.
                            </p>

                            <Handoff
                                icon={ArrowRight}
                                label='Next, Trade.'
                                text='Stored energy is offered into the hours that pay.'
                            />
                        </Stage>

                        <Stage
                            stage={trade}
                            pinned={pinned}
                            visualClassName='lg:self-center'
                            visual={<TradeVisual />}
                        >
                            <div className='flex items-center justify-between gap-4'>
                                <CardTag icon={ChartColumn} label='Trade' />
                                <span className='font-mono text-xs text-muted-foreground'>03 / 03</span>
                            </div>

                            <h3 className='mt-6 max-w-md font-heading text-3xl font-light leading-tight tracking-[-0.03em] text-foreground sm:text-4xl lg:text-3xl xl:text-4xl'>
                                Sell every megawatt in the hours that pay.
                            </h3>

                            <p className='mt-4 max-w-md text-[15px] leading-relaxed text-muted-foreground'>
                                Gridora ranks every window by price, risk and grid limits, then drafts the bid. Today that is an expected €41,200 more than flat selling.
                            </p>

                            <Handoff
                                icon={Repeat}
                                label='Back to Forecast.'
                                text="Every settled bid sharpens tomorrow's forecast."
                            />
                        </Stage>

                        <div
                            data-panel=''
                            className={cn('pt-4', pinned && 'lg:flex lg:h-full lg:w-[24rem] lg:shrink-0 lg:flex-col lg:pt-0')}
                        >
                            <p className='text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground'>
                                Running underneath
                            </p>

                            <ul className={cn('mt-4 grid gap-4 md:grid-cols-3', pinned && 'lg:flex lg:min-h-0 lg:flex-1 lg:flex-col lg:gap-3')}>
                                {extras.map((item) => {
                                    const Icon = item.icon

                                    return (
                                        <SpotlightCard
                                            as='li'
                                            key={item.title}
                                            className={cn('rounded-[1.5rem] bg-white p-6 ring-1 ring-black/5', pinned && 'lg:flex-1 lg:p-5')}
                                        >
                                            <div className='flex items-center gap-3'>
                                                <span className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-accent text-accent-foreground'>
                                                    <Icon className='size-5' aria-hidden='true' />
                                                </span>

                                                <h3 className='text-base font-medium text-foreground'>
                                                    {item.title}
                                                </h3>
                                            </div>

                                            <p className='mt-3 text-sm leading-relaxed text-muted-foreground'>
                                                {item.text}
                                            </p>
                                        </SpotlightCard>
                                    )
                                })}
                            </ul>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    )
}

export default SolutionsSection
